import { computed, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'

import type { ComputedRef, Ref } from 'vue'

import { useTasksKanbanStore } from './store'
import { useTasksKanbanLogsApi } from '../api/logs.hook'
import { parseLogFilterParams } from './filters/parse-log-filter-params'

interface UseKanbanLogsInfiniteScrollOptions {
  id: Ref<string> | ComputedRef<string>
}

interface UseKanbanLogsInfiniteScrollReturn {
  isLoadingMore: Ref<boolean>
  hasMore: ComputedRef<boolean>
}

export const useKanbanLogsInfiniteScroll = ({ id }: UseKanbanLogsInfiniteScrollOptions): UseKanbanLogsInfiniteScrollReturn => {
  const tasksKanbanStore = useTasksKanbanStore()
  const route = useRoute()

  const isLoadingMore = ref<boolean>(false)
  const hasMore = computed<boolean>(() => tasksKanbanStore.getHasMoreLogs)

  const loadMore = async (): Promise<void> => {
    const cursor = tasksKanbanStore.getLogsPagination.next_cursor
    if (!cursor || !id.value) return

    isLoadingMore.value = true

    try {
      await useTasksKanbanLogsApi({
        id: id.value,
        params: parseLogFilterParams(route.query),
        cursor,
      })
    } finally {
      isLoadingMore.value = false
    }
  }

  useInfiniteScroll(
    window,
    loadMore,
    {
      distance: 200,
      canLoadMore: () => hasMore.value && !isLoadingMore.value,
    },
  )

  return {
    isLoadingMore,
    hasMore,
  }
}
